import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * NativeBridge: Utilidad para detectar la plataforma y usar 
 * funciones nativas de forma segura sin romper la versión web.
 */
const NativeBridge = {
    // Detectar si estamos en un dispositivo móvil real (Android o iOS)
    isNative: Capacitor.isNativePlatform(),

    // Obtener información del dispositivo
    getDeviceInfo: async () => {
        if (!NativeBridge.isNative) return { model: 'Navegador Web', platform: 'web' };
        return await Device.getInfo();
    },

    // Wrapper para Geolocalización
    getCurrentPosition: async () => {
        try {
            if (NativeBridge.isNative) {
                // GPS Nativo (más preciso y estable en celulares)
                const position = await Geolocation.getCurrentPosition({
                    enableHighAccuracy: true,
                    timeout: 10000
                });
                return {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy
                };
            } else {
                // GPS Web estándar (para cuando abren el portal desde el browser)
                return new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        (err) => reject(err),
                        { enableHighAccuracy: true }
                    );
                });
            }
        } catch (error) {
            console.error("Error obteniendo posición:", error);
            throw error;
        }
    },

    // Solicitar permisos de GPS y Notificaciones (Solo necesario en apps nativas)
    requestPermissions: async () => {
        if (NativeBridge.isNative) {
            try {
                console.log("🚀 [NativeBridge] Solicitando permisos...");
                
                // 1. Pedir permisos de GPS (Esto pedirá Fine/Coarse en Android)
                const geoStatus = await Geolocation.requestPermissions();
                console.log("📍 GPS Status:", geoStatus.location);

                // 2. Pedir permisos de Notificaciones Push (Solo si no es iOS o si no da error)
                let pushStatus = { receive: 'granted' };
                if (Capacitor.getPlatform() !== 'ios') {
                    try {
                        pushStatus = await PushNotifications.requestPermissions();
                        console.log("🔔 Push Status:", pushStatus.receive);
                    } catch (e) {
                        console.warn("Fallo al pedir notificaciones en NativeBridge:", e);
                    }
                }
                
                return geoStatus.location === 'granted' && pushStatus.receive === 'granted';
            } catch (e) {
                console.error("Fallo al pedir permisos nativos", e);
                return false;
            }
        }
        return true; 
    },

    // Verificar si el GPS está activo y con permisos
    checkPermissions: async () => {
        if (NativeBridge.isNative) {
            try {
                const geoStatus = await Geolocation.checkPermissions();
                
                let pushStatus = { receive: 'granted' };
                if (Capacitor.getPlatform() !== 'ios') {
                    try {
                        pushStatus = await PushNotifications.checkPermissions();
                    } catch (e) {
                        console.warn("Fallo al consultar push en NativeBridge:", e);
                    }
                }
                
                return {
                    gps: geoStatus.location,
                    push: pushStatus.receive
                };
            } catch (e) {
                console.error("Error en checkPermissions nativo:", e);
                return { gps: 'denied', push: 'denied' };
            }
        }
        return { gps: 'granted', push: 'granted' };
    },

    // Iniciar monitoreo continuo de posición
    watchPosition: async (options, callback) => {
        if (NativeBridge.isNative) {
            try {
                return await Geolocation.watchPosition(options, (pos, err) => {
                    if (err) {
                        callback(null, err);
                    } else if (pos) {
                        callback({
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                            accuracy: pos.coords.accuracy
                        }, null);
                    }
                });
            } catch (e) {
                console.error("Error en watchPosition nativo:", e);
                return null;
            }
        } else {
            if (navigator.geolocation) {
                return navigator.geolocation.watchPosition(
                    (pos) => callback({ lat: pos.coords.latitude, lng: pos.coords.longitude }, null),
                    (err) => callback(null, err),
                    options
                );
            }
            return null;
        }
    },

    // Detener monitoreo continuo
    clearWatch: async (watchId) => {
        if (NativeBridge.isNative) {
            try {
                if (watchId) {
                    await Geolocation.clearWatch({ id: watchId });
                }
            } catch (e) {
                console.error("Error en clearWatch nativo:", e);
            }
        } else {
            if (navigator.geolocation && watchId) {
                navigator.geolocation.clearWatch(watchId);
            }
        }
    }
};

export default NativeBridge;
